import React from 'react';
import useCareTeamMember from '../../../../../hooks/api/useCareTeamMember';
import { getIntl } from '../../../../../utils/intl';
import { useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import CareTeamMemberCard from '../../../../shared/CareTeamMemberCard';
import { APITypes } from '../../../../../primary-record-types';
import Stack, { ContentStack, contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import Text from '../../../../v2BaseComponents/Text';
import { StandardPrompt } from '../../../../v2BaseComponents/Prompt';
import { getCareTeamMemberDescriptor } from '../../../../../utils/careTeamUtils';
import { LoadableAvatar, getFallbackForGenericString, getFallbackFromName } from '../../../../v2BaseComponents/Avatar';
import urls from '../../../../../utils/urls';
import { usePrevious } from '../../../../../hooks/usePrevious';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';

const intl = getIntl('relatedCareTeamList', {
    relatedCareTeam: 'Related Care Team Members',
    add: 'Add',
    view: 'View Details',
    remove: 'Remove From List',
    relatedCareTeamMember: 'Related Care Team Member',
});

export type RelatedCareTeamOwnProps = {
    careTeamMemberId: string;
    profileId: string;
    relatedCareTeamMembers: APITypes.medicalProfileService.ListRelatedCareTeamMembersResponse;
};

export type RelatedCareTeamProps = RelatedCareTeamOwnProps;

const getCareTeamMemberDetails = (
    profileId: string,
    careTeamMember: APITypes.medicalProfileService.CareTeamMemberDetails,
) => {
    const careTeamMemberDescriptor = getCareTeamMemberDescriptor(careTeamMember);
    const fallbackName =
        careTeamMemberDescriptor.type === 'name'
            ? getFallbackFromName(careTeamMemberDescriptor.firstName, careTeamMemberDescriptor.lastName)
            : getFallbackForGenericString(careTeamMemberDescriptor.text);
    const minimalDescriptor =
        careTeamMemberDescriptor.type === 'name'
            ? careTeamMemberDescriptor.firstName + ' ' + careTeamMemberDescriptor.lastName
            : careTeamMemberDescriptor.text;

    return {
        fallbackName,
        minimalDescriptor,
        avatarUrl: urls.api.careTeamMembers.avatar(profileId, careTeamMember.careTeamMemberId),
    };
};

const RelatedCareTeam: React.FC<RelatedCareTeamProps> = ({ careTeamMemberId, profileId, relatedCareTeamMembers }) => {
    const navigation = useProfilesTabNavigation();
    const { removeRelatedCareTeamMember, isRemovingRelatedCareTeamMember } = useCareTeamMember({
        careTeamMemberId,
        medicalProfileId: profileId,
    });
    const [focusedRelatedCareTeamMemberId, setFocusedRelatedCareTeamMemberId] = React.useState<string | null>(null);
    const focusedRelatedCareTeamMember =
        (relatedCareTeamMembers?.relatedCareTeamMembers || []).find(
            (_) => !!focusedRelatedCareTeamMemberId && _.careTeamMemberId === focusedRelatedCareTeamMemberId,
        ) || null;
    const previousFocusedRelatedCareTeamMemberId = usePrevious(focusedRelatedCareTeamMember);

    const promptFocusedCareTeamMember = focusedRelatedCareTeamMember || previousFocusedRelatedCareTeamMemberId;
    const focusedDetails = promptFocusedCareTeamMember
        ? getCareTeamMemberDetails(profileId, promptFocusedCareTeamMember)
        : null;

    if (!relatedCareTeamMembers?.relatedCareTeamMembers?.length) return null;

    return (
        <ContentStack>
            <Text variant="h4">{intl('relatedCareTeam')}</Text>
            {(relatedCareTeamMembers.relatedCareTeamMembers || []).map((careTeamMember) => (
                <CareTeamMemberCard
                    key={careTeamMember.careTeamMemberId}
                    careTeamMember={careTeamMember}
                    medicalProfileId={profileId}
                    onPress={() => setFocusedRelatedCareTeamMemberId(careTeamMember.careTeamMemberId)}
                    isDisabled={isRemovingRelatedCareTeamMember}
                />
            ))}
            <StandardPrompt
                title={intl('relatedCareTeamMember')}
                isOpen={!!focusedRelatedCareTeamMember}
                setIsOpen={(isOpen) => !isOpen && setFocusedRelatedCareTeamMemberId(null)}
                actions={[
                    {
                        title: intl('view'),
                        onPress: () => {
                            navigation.navigate('care-team-member', {
                                profileId: profileId,
                                careTeamMemberId: promptFocusedCareTeamMember!.careTeamMemberId,
                            });
                            setFocusedRelatedCareTeamMemberId(null);
                        },
                        isDisabled: isRemovingRelatedCareTeamMember,
                        icon: 'ArrowRight',
                    },
                    {
                        title: intl('remove'),
                        onPress: () => {
                            removeRelatedCareTeamMember(focusedRelatedCareTeamMemberId!);
                            setFocusedRelatedCareTeamMemberId(null);
                        },
                        isDisabled: isRemovingRelatedCareTeamMember,
                        variant: 'alert',
                        icon: 'Trash',
                    },
                ]}
            >
                {promptFocusedCareTeamMember ? (
                    <MainContentContainer>
                        {focusedDetails ? (
                            <Stack vertical fullWidth space={contentStackSpacing} align="center">
                                <LoadableAvatar
                                    avatarUrl={focusedDetails.avatarUrl}
                                    fallbackName={focusedDetails.fallbackName}
                                    size="xxl"
                                />
                                <Text variant="body-1" alignText="center">
                                    {focusedDetails.minimalDescriptor}
                                </Text>
                            </Stack>
                        ) : null}
                    </MainContentContainer>
                ) : null}
            </StandardPrompt>
        </ContentStack>
    );
};

export default React.memo(RelatedCareTeam);
